import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../../context/I18nContext";
import { pathFor } from "../../lib/routes";
import {
  CONSENT_STORAGE_KEY,
  DEFAULT_CONSENT,
  readStoredConsent,
  writeStoredConsent,
} from "../../lib/consent";

export default function CookieBanner({ openSignal }) {
  const { t, lang } = useI18n();
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_CONSENT);

  useEffect(() => {
    const stored = readStoredConsent();
    if (!stored) {
      setVisible(true);
    } else {
      setPrefs(stored);
    }
  }, []);

  useEffect(() => {
    if (openSignal > 0) {
      setPrefs(readStoredConsent() || DEFAULT_CONSENT);
      setVisible(true);
      setShowPrefs(true);
    }
  }, [openSignal]);

  const acceptAll = () => {
    writeStoredConsent({
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
    });
    setVisible(false);
    setShowPrefs(false);
  };

  const rejectAll = () => {
    writeStoredConsent({ ...DEFAULT_CONSENT });
    setVisible(false);
    setShowPrefs(false);
  };

  const saveCustom = () => {
    writeStoredConsent({ ...prefs, necessary: true });
    setVisible(false);
    setShowPrefs(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t("cookie.title")}
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-50 bg-white border-t border-slate-200 shadow-[0_-10px_30px_-15px_rgba(15,23,42,0.15)]"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-5">
        {!showPrefs ? (
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1">
              <h3
                className="text-slate-900 font-semibold text-base"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                {t("cookie.title")}
              </h3>
              <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                {t("cookie.body")}{" "}
                <Link
                  to={pathFor("cookies", lang)}
                  data-testid="cookie-banner-policy-link"
                  className="text-emerald-700 underline"
                >
                  {t("cookie.policyLink")}
                </Link>
              </p>
            </div>
            <div className="flex flex-wrap gap-2 md:justify-end">
              <button
                type="button"
                onClick={() => setShowPrefs(true)}
                data-testid="cookie-customize-button"
                className="inline-flex items-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
              >
                {t("cookie.customize")}
              </button>
              <button
                type="button"
                onClick={rejectAll}
                data-testid="cookie-reject-button"
                className="inline-flex items-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
              >
                {t("cookie.rejectAll")}
              </button>
              <button
                type="button"
                onClick={acceptAll}
                data-testid="cookie-accept-button"
                className="inline-flex items-center rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
              >
                {t("cookie.acceptAll")}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <h3
              className="text-slate-900 font-semibold text-base"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              {t("cookie.customize")}
            </h3>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              {[
                { key: "necessary", disabled: true },
                { key: "analytics", disabled: false },
                { key: "marketing", disabled: false },
                { key: "functional", disabled: false },
              ].map(({ key, disabled }) => (
                <label
                  key={key}
                  className="flex items-start gap-3 rounded-md border border-slate-200 p-3 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={prefs[key]}
                    disabled={disabled}
                    onChange={(e) =>
                      setPrefs((p) => ({ ...p, [key]: e.target.checked }))
                    }
                    data-testid={`cookie-pref-${key}`}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span>
                    <span className="block text-sm font-semibold text-slate-900">
                      {t(`cookie.categories.${key}Title`)}
                    </span>
                    <span className="block text-xs text-slate-600 leading-relaxed">
                      {t(`cookie.categories.${key}Desc`)}
                    </span>
                  </span>
                </label>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-2 justify-end">
              <button
                type="button"
                onClick={rejectAll}
                data-testid="cookie-prefs-reject-button"
                className="inline-flex items-center rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
              >
                {t("cookie.rejectAll")}
              </button>
              <button
                type="button"
                onClick={saveCustom}
                data-testid="cookie-save-button"
                className="inline-flex items-center rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
              >
                {t("cookie.save")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
